import { bool } from "@techstark/opencv-js";
import {DocumentBasylic, GarantMonoBloc, NOTIFICATION_ACTIONS, Type_garantie} from "shared/types/types";
import {Categorie_pieces, Section_Locataire, Garant} from "shared/types/types";
import { ASSISTANCE_PRICING, CREDITS_PRICING, ID_TRACKER_PRICING } from "shared/config/pricings";
import { joursRestantsUtil, remainDateUtil, setDateAAAAMMJJ } from "shared/lib/date_utils";

export const extractNumber = (str: string): number | null => {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
};

export const formattedStringToDate = (str: string): string => {
    return setDateAAAAMMJJ(str);
};

export const remainDate = (dateStr: string) => {
    return remainDateUtil(dateStr)
}

export const joursRestants = (dateCible = '') => {
    return joursRestantsUtil(dateCible);
}

export const formatShareIDVideoResultToDocument = (document: any) => {
    const { doc_num, surname, name, sex, expiration_date, issuance_date, mrz_1, mrz_2, birth_date, country_code, birth_place } =
        document.document.ocr;
    const documentFormatted: DocumentBasylic = {
        id: document.id,
        // TODO: vérifier la validité de ces valeurs
        conformite: true,
        darkWeb: false,
        country_code: document.document.type.country_code,
        dateDelivrance: issuance_date ?  formattedStringToDate(issuance_date ?? "") : "",
        dateExpiration: expiration_date ? formattedStringToDate(expiration_date ?? "") : "",
        dateNaissance: birth_date ? formattedStringToDate(birth_date ?? "") : "",
        nom: surname,
        nomUsage: surname,
        numeroDocument: doc_num,
        paysNaissance: country_code,
        lieuNaissance: birth_place,
        prenom: name,
        sexe: sex,
        typeDocument: document.document.type.doc_type,
        mrz: mrz_1,
        viz: mrz_2,
    };
    return documentFormatted;
};

export const formatShareIDPhotoResultToDocument = (externalApiResponse: any) => {
    const { doc_num, surname, name, sex, expiration_date, issuance_date, mrz_1, mrz_2, birth_date, country_code, birth_place } =
        externalApiResponse.ocr;
    const documentFormatted: DocumentBasylic = {
        id: externalApiResponse.id,
        // TODO: vérifier la validité de ces valeurs
        conformite: externalApiResponse.verdict,
        darkWeb: false,
        country_code: externalApiResponse.country_code,
        dateDelivrance: issuance_date ?  formattedStringToDate(issuance_date ?? "") : "",
        dateExpiration: expiration_date ? formattedStringToDate(expiration_date ?? "") : "",
        dateNaissance: birth_date ? formattedStringToDate(birth_date ?? "") : "",
        nom: surname,
        nomUsage: surname,
        numeroDocument: doc_num,
        paysNaissance: country_code,
        lieuNaissance: birth_place,
        prenom: name,
        sexe: sex,
        typeDocument: externalApiResponse.doc_type,
        mrz: mrz_1,
        viz: mrz_2,
    };
    return documentFormatted;
};



// Mapping of tabs to notification actions
export const tabNotificationActions: Record<string, NOTIFICATION_ACTIONS[]> = {
    "identity-documents": [
        NOTIFICATION_ACTIONS.Document_View_Request,
        NOTIFICATION_ACTIONS.Document_View_Request_New_Status,
        NOTIFICATION_ACTIONS.Document_Lost_Declaration,
        NOTIFICATION_ACTIONS.Document_Delete,
        NOTIFICATION_ACTIONS.Document_Delete_Followed,
        NOTIFICATION_ACTIONS.Document_New_Followed,
        NOTIFICATION_ACTIONS.Document_Change_Followed_State,
        NOTIFICATION_ACTIONS.Folder_Notification,
    ],
    "id-locker": [
        NOTIFICATION_ACTIONS.Folder_Creation,
        NOTIFICATION_ACTIONS.Folder_Delete,
        NOTIFICATION_ACTIONS.File_Added,
        NOTIFICATION_ACTIONS.File_Delete,
        NOTIFICATION_ACTIONS.Invitation_Send,
        NOTIFICATION_ACTIONS.Permission_Deleted,
        NOTIFICATION_ACTIONS.Permission_Changed,
        NOTIFICATION_ACTIONS.Visibility_Changed,
        NOTIFICATION_ACTIONS.Expired_Date_Changed,
    ],
    "id-assistant": [
        NOTIFICATION_ACTIONS.Accompaniment_New_Status,
        NOTIFICATION_ACTIONS.Accompaniment_New,
        NOTIFICATION_ACTIONS.Accompaniment_Delete,
    ],
};


export const generateRandomPassword = (length = 12) => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const specialChars = "!@#$%";

    const allChars = lowercase + uppercase + numbers + specialChars;

    let password = "";

    // Ensure at least one of each required type
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password to avoid predictable patterns
    password = password.split('').sort(() => 0.5 - Math.random()).join('');

    return password;
};

// Variantes pour l’animation
export const stepVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
    transition: { duration: 0.3, ease: "easeInOut" as const },
};

export const statusLabels: Record<Status, string> = {
    lost: 'Perdus',
    followed: 'Suivis',
    darkWeb: 'Dark Web',
    notFound: 'Inconnu',
}

export const statusColors: Record<Status, string> = {
    lost: '#e74c3c',
    followed: '#3498db',
    darkWeb: '#090909',
    notFound: '#bdbba0',
}

export type Status = 'lost' | 'followed' | 'darkWeb' | 'notFound'
export type DocType = 'passeport' | 'id' | 'permis' | 'sejour' | 'hc' | 'none' | string

export const docLabels: Record<DocType, string> = {
    passeport: 'passeport',
    permis: 'Permis',
    id: 'CNI',
    none: 'inconnu',
    sejour: 'séjour',
    hc: 'vitale',
}


export const formatDocumentType = (value: string) => {
    switch (value.toLowerCase()) {
        case "id":
        case "id_card":
            return "Carte d'identité";
        case "passeport":
        case "passport":
            return "Passeport";
        case "permis":
        case "driver_permit":
        case "driving-licence":
            return "Permis de conduire";
        case "sejour":
        case "residency_permit":
            return "Carte de séjour";
        default:
            return "Inconnu";
    }
};

export const getOfferPlan = (offerHeadRoute?: string, offerId?: number) => {
    if (!offerHeadRoute || offerId === null || offerId === undefined) {
        return ;
    }
    switch (offerHeadRoute) {
        // Recherche par offreId plutôt que par position : reproduit exactement
        // l'ancien appariement (6→[0], 7→[1], 8→[2] ; 1→[0], 4→[1], 5→[2],
        // 2→[3], 3→[4]) et couvre en plus les offres 2026 sans dépendre de
        // l'ordre du tableau. Une offre retirée de la vente (freeze) reste
        // trouvable : les clients déjà abonnés doivent voir leur souscription.
        case 'accompaniment':
            return ASSISTANCE_PRICING.find((plan) => plan.offreId === offerId);
        case 'id-tracker':
            return ID_TRACKER_PRICING.find((plan) => plan.offreId === offerId);
        case 'credits':
            if (offerId === 0) {
                return CREDITS_PRICING[0];
            }
            if (offerId === 1) {
                return CREDITS_PRICING[1];
            }
            if (offerId === 2) {
                return CREDITS_PRICING[2];
            }
            break;
    }

}

export const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=fr.idprotect.androidapplication&utm_source=emea_Med"
export const APPLE_STORE_LINK = "https://apps.apple.com/fr/app/id-protect/id6449357912"